import { supabase } from './supabase'
import { appConfig } from './config'

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message)
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const { data } = await supabase.auth.getSession()
  const headers = new Headers(init.headers)
  if (!headers.has('Content-Type') && init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json')
  if (data.session?.access_token) headers.set('Authorization', `Bearer ${data.session.access_token}`)

  const response = await fetch(`${appConfig.apiUrl}${path.startsWith('/') ? path : `/${path}`}`, {
    ...init,
    headers,
  })

  const contentType = response.headers.get('content-type') || ''
  const body = contentType.includes('application/json') ? await response.json() : await response.text()
  if (!response.ok) {
    const message = typeof body === 'object' && body && 'message' in body
      ? Array.isArray(body.message) ? body.message.join(', ') : String(body.message)
      : `Request failed with status ${response.status}`
    throw new ApiError(message, response.status, body)
  }
  return body as T
}

export const apiGet = <T>(path: string) => api<T>(path)
export const apiPost = <T>(path: string, data?: unknown) => api<T>(path, { method: 'POST', body: data === undefined ? undefined : JSON.stringify(data) })
export const apiFormPost = <T>(path: string, data: FormData) => api<T>(path, { method: 'POST', body: data })
export const apiPut = <T>(path: string, data?: unknown) => api<T>(path, { method: 'PUT', body: data === undefined ? undefined : JSON.stringify(data) })
export const apiPatch = <T>(path: string, data?: unknown) => api<T>(path, { method: 'PATCH', body: data === undefined ? undefined : JSON.stringify(data) })
export const apiDelete = <T>(path: string) => api<T>(path, { method: 'DELETE' })

export async function apiBlob(path: string): Promise<Blob> {
  const { data } = await supabase.auth.getSession()
  const headers = new Headers()
  if (data.session?.access_token) headers.set('Authorization', `Bearer ${data.session.access_token}`)
  const response = await fetch(`${appConfig.apiUrl}${path.startsWith('/') ? path : `/${path}`}`, { headers })
  if (!response.ok) throw new ApiError(await response.text(), response.status)
  return response.blob()
}

/** POSTs a form and returns the file the API sends back, e.g. a generated presentation. */
export async function apiFormBlob(path: string, data: FormData): Promise<Blob> {
  const { data: auth } = await supabase.auth.getSession()
  const headers = new Headers()
  if (auth.session?.access_token) headers.set('Authorization', `Bearer ${auth.session.access_token}`)
  const response = await fetch(`${appConfig.apiUrl}${path.startsWith('/') ? path : `/${path}`}`, {
    method: 'POST',
    headers,
    body: data,
  })
  if (!response.ok) {
    const body = await response.json().catch(() => null)
    const message = body && typeof body === 'object' && 'message' in body
      ? Array.isArray(body.message) ? body.message.join(', ') : String(body.message)
      : `Request failed with status ${response.status}`
    throw new ApiError(message, response.status, body)
  }
  return response.blob()
}

export async function apiDownload(path: string, fallbackName = 'download'): Promise<void> {
  const { data } = await supabase.auth.getSession()
  const headers = new Headers()
  if (data.session?.access_token) headers.set('Authorization', `Bearer ${data.session.access_token}`)
  const response = await fetch(`${appConfig.apiUrl}${path.startsWith('/') ? path : `/${path}`}`, { headers })
  if (!response.ok) {
    const body = await response.text()
    throw new ApiError(body || `Download failed with status ${response.status}`, response.status)
  }
  const blob = await response.blob()
  const disposition = response.headers.get('content-disposition') || ''
  const match = disposition.match(/filename="?([^";]+)"?/i)
  const name = match?.[1] || fallbackName
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = name
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}
